/**
 * NAFDistributionWidget — Camembert "Répartition des membres par activité NAF"
 *
 * Affiche le top N codes NAF + un segment "Autres" agrégé.
 * Backend : GET /api/admin/stats/naf-distribution?top=10
 *
 * Visibilité : Super Admin / Admin dashboard.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, PieChart as PieChartIcon, TrendingUp, Building2 } from 'lucide-react';
import { getAuthHeader } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Palette de couleurs (cohérente avec les vignettes du dashboard)
const COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
  '#14b8a6', // teal
];
const OTHERS_COLOR = '#94a3b8'; // slate-400

/**
 * Donut SVG sans dépendance (recharts/chart.js non requis).
 * Reçoit une liste de segments {value, color, label}.
 */
function DonutChart({ segments, size = 200, strokeWidth = 36 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Répartition NAF" role="img">
      {/* Background ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={strokeWidth}
      />
      {segments.map((seg, i) => {
        const length = (seg.value / total) * circumference;
        const dashArray = `${length} ${circumference - length}`;
        const dashOffset = circumference - offset;
        const el = (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 0.4s ease-out' }}
          >
            <title>{`${seg.label} — ${seg.value}`}</title>
          </circle>
        );
        offset += length;
        return el;
      })}
      {/* Total au centre */}
      <text x={size / 2} y={size / 2 - 6} textAnchor="middle" className="fill-slate-900" style={{ fontSize: 26, fontWeight: 700 }}>
        {total}
      </text>
      <text x={size / 2} y={size / 2 + 16} textAnchor="middle" className="fill-slate-500" style={{ fontSize: 11 }}>
        membres
      </text>
    </svg>
  );
}

export default function NAFDistributionWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/stats/naf-distribution?top=10`, {
          headers: getAuthHeader(),
        });
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Impossible de charger la répartition NAF');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card data-testid="naf-distribution-widget">
        <CardContent className="py-12 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Chargement de la répartition NAF…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="naf-distribution-widget" className="border-red-200 bg-red-50/40">
        <CardContent className="py-6 text-sm text-red-700">
          ⚠️ {error}
        </CardContent>
      </Card>
    );
  }

  if (!data || data.with_naf === 0) {
    return (
      <Card data-testid="naf-distribution-widget" className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <PieChartIcon className="h-4 w-4 text-indigo-600" />
            Répartition des membres par activité NAF
          </CardTitle>
          <CardDescription>Aucun membre n&apos;a encore de code NAF renseigné.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const segments = data.top.map((item, i) => ({
    label: `${item.code} — ${item.label || 'Activité non répertoriée'}`,
    value: item.count,
    color: COLORS[i % COLORS.length],
    code: item.code,
    fullLabel: item.label,
    pct: item.pct,
  }));
  if (data.others_count > 0) {
    segments.push({
      label: `Autres (${data.others_count})`,
      value: data.others_count,
      color: OTHERS_COLOR,
      code: 'others',
      fullLabel: 'Autres codes NAF',
      pct: data.others_pct,
    });
  }

  const coveragePct = data.total_members
    ? Math.round((data.with_naf / data.total_members) * 100)
    : 0;

  return (
    <Card data-testid="naf-distribution-widget" className="border-slate-200 hover:shadow-md transition-all">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChartIcon className="h-4 w-4 text-indigo-600" />
              Répartition des membres par activité NAF
            </CardTitle>
            <CardDescription className="mt-1">
              Top 10 codes NAF — utile pour adapter l&apos;offre de garanties RCDS.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-indigo-700 border-indigo-800 text-white font-semibold shadow-sm hover:bg-indigo-800">
              <Building2 className="h-3 w-3 mr-1" />
              {data.with_naf} / {data.total_members} membres avec NAF ({coveragePct}%)
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start">
          {/* Donut */}
          <div className="flex justify-center md:justify-start">
            <DonutChart segments={segments} size={200} strokeWidth={36} />
          </div>

          {/* Legend */}
          <ul className="space-y-1.5" data-testid="naf-distribution-legend">
            {segments.map((seg, i) => (
              <li
                key={seg.code + i}
                className="flex items-start gap-2.5 text-sm py-1 border-b border-slate-100 last:border-b-0"
              >
                <span
                  className="shrink-0 h-3 w-3 rounded-sm mt-1"
                  style={{ background: seg.color }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-slate-800 text-xs">{seg.code}</span>
                    <span className="text-xs text-slate-500 tabular-nums shrink-0">
                      {seg.value} <span className="text-slate-400">·</span> {seg.pct}%
                    </span>
                  </div>
                  {seg.fullLabel && (
                    <div className="text-[11px] text-slate-500 line-clamp-1">{seg.fullLabel}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Insight footer */}
        {segments[0] && (
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-600">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Secteur dominant : <strong className="font-mono">{segments[0].code}</strong>
              {segments[0].fullLabel && <> — {segments[0].fullLabel}</>}
              {' '}({segments[0].value} membres, {segments[0].pct}%).
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
